import React, { useState } from "react";
import useUploadFiles from "../../../services/hooks/useUploadFiles";

// components
import Upload from "../../../component/modals/upload";
import TextBox from "../../../component/element/forms/TextBox";
import DateInput from "../../../component/element/forms/DateInput";

import TextInput from "../../../component/element/forms/TextInput";
import SelectOption from "../../../component/element/forms/SelectOption";

// state
import {
  create_opportunity,
  get_opportunities_type,
} from "../../../services/apis/opportunity";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  ICreateOpportunity,
  ICreateOpportunityError,
} from "../../../store/types/opportunity";
import Modal from "../../../component/ui/modal";
import DocumentUpload from "../../../component/modals/upload/DocumentUpload";
import { Image } from "lucide-react";

type UploadedFile = {
  id: string;
  url: string;
  path: string;
};

type ICreateOpportunityType = {
  show: boolean;
  onHandler: () => void;
};

const CreateOpportunity: React.FC<ICreateOpportunityType> = ({
  show,
  onHandler,
}) => {
  const dispatch = useAppDispatch();
  const {
    loader: imgLoader,
    uploadFile: uploadImageFile,
    deleteFiles: deleteImageFiles,
  } = useUploadFiles();
  const { uploadFile: uploadDocFile, deleteFiles: deleteDocFiles } =
    useUploadFiles();
  const [imgPath, setImgPath] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const types = useAppSelector(
    ({ opportunity }) => opportunity.opportunity_types,
  );

  // const [loading] = useState(false);

  const [data, dataSet] = React.useState<ICreateOpportunity>({
    opportunity_name: "",
    opportunity_files: [],
    opportunity_description: "",
    expire_date: "",
    opportunity_image: "",
    opportunity_type: "",
  });

  const [error, errorSet] = React.useState<ICreateOpportunityError>({
    opportunity_name: "",
    opportunity_files: "",
    opportunity_description: "",
    expire_date: "",
    opportunity_image: "",
  });

  const clear = () => {
    dataSet({
      opportunity_name: "",
      opportunity_files: [],
      opportunity_description: "",
      expire_date: "",
      opportunity_image: "",
      opportunity_type: "",
    });
    setUploadedFiles([]);
  };

  const handleOk = async () => {
    let err;
    if (!data.opportunity_name) {
      // errorSet((prev) => ({ ...prev, opportunity_name: "warning" }));
      err = true;
    }
    if (!data.opportunity_description) {
      err = true;
    }
    if (!data.opportunity_image) {
      err = true;
    }
    if (!data.opportunity_files || data.opportunity_files.length === 0) {
      errorSet((prev) => ({
        ...prev,
        opportunity_files: "error: this field is required",
      }));
      err = true;
    }
    console.log("error", err, data);
    if (err) return;
    const response = await create_opportunity(data, dispatch);
    if (response) {
      clear();
      onHandler();
    }
  };

  const onLoad = React.useCallback(() => {
    get_opportunities_type(dispatch);
  }, [dispatch]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  return (
    <Modal
      title="Create Opportunity"
      open={show}
      onClose={() => {
        clear();
        onHandler();
      }}
      onOkClick={handleOk}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <Upload
            icon={<Image />}
            title="Upload Image"
            label="JPG, PNG"
            name="opportunity_image"
            loading={imgLoader}
            status={error.opportunity_image}
            completed={Boolean(data.opportunity_image)}
            imgPreview={data.opportunity_image}
            onChange={async (fileOrFiles) => {
              const paths = await uploadImageFile(
                fileOrFiles as File,
                "opportunity_image",
              );

              if (paths.length > 0) {
                const img_path = paths[0].base_url + "/" + paths[0].path;
                dataSet((prev) => ({ ...prev, opportunity_image: img_path }));
                errorSet((prev) => ({ ...prev, opportunity_image: "" }));
                setImgPath(paths[0].path);
              }
            }}
            onDelete={async () => {
              if (imgPath) {
                const res = await deleteImageFiles(imgPath);
                if (res) {
                  dataSet((prev) => ({ ...prev, opportunity_image: "" }));
                }
              }
            }}
          />

          <DocumentUpload
            title="File Upload"
            name="opportunity_file"
            onChange={async (items) => {
              const files = items.map((i) => i.file);
              const paths = await uploadDocFile(files, "opportunity_file");
              if (paths.length > 0) {
                const newUploadedFiles = paths.map((p: any, index: number) => ({
                  id: items[index].id,
                  url: `${p.base_url}/${p.path}`,
                  path: p.path,
                }));

                setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);

                dataSet((prev) => ({
                  ...prev,
                  opportunity_files: [
                    ...prev.opportunity_files,
                    ...newUploadedFiles.map((f: any) => f.url),
                  ],
                }));
              }
            }}
            onDelete={async (fileId) => {
              const fileToDelete = uploadedFiles.find((f) => f.id === fileId);
              if (!fileToDelete) {
                return;
              }

              const res = await deleteDocFiles(fileToDelete.path);

              if (res) {
                setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));

                dataSet((prev) => ({
                  ...prev,
                  opportunity_files: prev.opportunity_files.filter(
                    (url) => url !== fileToDelete.url,
                  ),
                }));
              }
            }}
          />
        </div>

        <TextInput
          label="Opportunity Name"
          name="opportunity_name"
          placeholder="Opportunity Name"
          type="text"
          value={data.opportunity_name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;
            dataSet((prev) => ({ ...prev, [name]: value }));
          }}
        />

        <div className="inline-grid gap-6 w-full">
          <SelectOption
            items={types}
            label="Opportunity Type"
            placeholder="Select Type"
            value={data.opportunity_type}
            onChange={(selected) => {
              dataSet((prev) => ({
                ...prev,
                opportunity_type: selected?.toString(),
              }));
              // setError(prev =>({...prev, opportun}))
            }}
          />

          <DateInput
            label="Select Expire Date"
            onChange={(date) => {
              dataSet((prev) => ({
                ...prev,
                expire_date: date.split(" ")?.[0],
              }));
            }}
          />
        </div>

        <TextBox
          label="Opportunity Description"
          name="opportunity_description"
          value={data.opportunity_description}
          placeholder="Opportunity Description"
          onChange={(name, value) =>
            dataSet((prev) => ({ ...prev, [name]: value }))
          }
        />
      </div>
    </Modal>
  );
};

export default CreateOpportunity;
